import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDateTime } from '../lib/mockData';
import { cn } from '../service/CoreFunctions';
import { Clock, Users, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SplitDetailsModal = ({ open, onOpenChange, invoice }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  if (!invoice) return null;

  // The actual structure from cu0904
  const transactions = invoice.transactions || [];
  
  const paidTransactions = transactions.filter(t => t.statusid === "1");
  const paidAmount = paidTransactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  const totalAmount = Number(invoice.amount_total) || 0;
  const remainingAmount = totalAmount - paidAmount;
  const paymentProgress = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-xl max-h-[90vh] flex flex-col p-0 overflow-hidden border">
        <AlertDialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <AlertDialogTitle className="text-xl font-semibold tracking-tight">
                {t('splitTransactions')}
              </AlertDialogTitle>
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider font-medium">
                <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono border border-border/50">
                  {invoice.id}
                </span>
                <span className="text-border">•</span>
                <span>{invoice.txndesc}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted rounded-md text-[10px] font-bold border border-border/40">
              <Users className="h-3 w-3" />
              <span>{invoice.count} / {invoice.count_total}</span>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              <span>{t('paymentProgress') || 'Progress'}</span>
              <span>{paymentProgress.toFixed(0)}%</span>
            </div>
            <div className="h-1 w-full bg-muted rounded-full">
              <div 
                className="h-full bg-primary transition-all duration-700 rounded-full"
                style={{ width: `${paymentProgress}%` }}
              />
            </div>
          </div>
        </AlertDialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Minimal Stats Row */}
          <div className="px-6 py-4 grid grid-cols-3 gap-8 border-b bg-muted/5">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{t('totalAmount')}</p>
              <p className="text-sm font-bold">{formatCurrency(totalAmount, i18n.language, user?.currency)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-success font-bold mb-1">{t('paidAmount')}</p>
              <p className="text-sm font-bold text-success">{formatCurrency(paidAmount, i18n.language, user?.currency)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-warning font-bold mb-1">{t('remainingAmount')}</p>
              <p className="text-sm font-bold text-warning">{formatCurrency(remainingAmount, i18n.language, user?.currency)}</p>
            </div>
          </div>

          {/* Configuration Data */}
          <div className="px-6 py-3 border-b flex items-center justify-between text-[11px] text-muted-foreground bg-muted/10 font-medium">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="opacity-70">{t('eachAmount')}:</span>
                <span className="text-foreground font-semibold">{formatCurrency(invoice.amount, i18n.language, user?.currency)}</span>
              </div>
              <div className="w-px h-3 bg-border" />
              <div className="flex items-center gap-1.5">
                <span className="opacity-70">{t('totalSplitPerson')}:</span>
                <span className="text-foreground font-semibold uppercase">{invoice.count_total} {t('people')}</span>
              </div>
            </div>
            <span>{formatDateTime(invoice.created_at, i18n.language)}</span>
          </div>

          {/* List of transactions */}
          <div className="divide-y border-b">
            {transactions.length > 0 ? (
              transactions.map((txn, index) => (
                <div key={txn.id || index} className="px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      txn.statusid === "1" ? "bg-success" : "bg-muted-foreground/30"
                    )} />
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-foreground">
                          {formatCurrency(txn.amount, i18n.language, user?.currency)}
                        </span>
                        {txn.description && (
                          <span className="text-sm text-muted-foreground">• {txn.description}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-mono uppercase tracking-tight">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(txn.callbacked_at || txn.created_at, i18n.language)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1 text-right">
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-widest",
                      txn.statusid === "1" ? "text-success" : "text-muted-foreground"
                    )}>
                      {txn.statusid === "1" 
                        ? (txn.settle_at ? t('settled') : t('waitingForSettlement'))
                        : t('pendingPayment')}
                    </span>
                    
                    {txn.statusid === "1" && !txn.settle_at && (
                      <div className="w-1 h-1 rounded-full bg-warning animate-pulse" />
                    )}
                    {txn.statusid === "1" && txn.settle_at && (
                      <Check className="h-2.5 w-2.5 text-success opacity-70" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 text-center text-muted-foreground italic text-sm">
                {t('noSplitData') || 'No split transaction data available.'}
              </div>
            )}
          </div>
        </div>

        <AlertDialogFooter className="p-4 sm:justify-center border-t">
          <AlertDialogCancel className="w-full sm:w-auto h-9 text-xs font-semibold">
            {t('close') || 'Close'}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SplitDetailsModal;
